import { useCallback, useEffect, useRef } from 'react'

export type DebouncedCallback<T extends (...args: never[]) => void> = T & {
  cancel: () => void
}

export function useDebouncedCallback<T extends (...args: never[]) => void>(
  callback: T,
  delay: number,
): DebouncedCallback<T> {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const callbackRef = useRef(callback)
  callbackRef.current = callback

  const cancel = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  useEffect(() => () => cancel(), [cancel])

  const debounced = useCallback(
    ((...args: Parameters<T>) => {
      cancel()
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null
        callbackRef.current(...args)
      }, delay)
    }) as DebouncedCallback<T>,
    [delay, cancel],
  )

  debounced.cancel = cancel
  return debounced
}
